// hooks/useCustomers.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { customersApi } from '@/api/customers'
import type {
	CustomerFilter,
	CreateCustomerRequest,
	UpdateCustomerRequest,
	TransferCustomerRequest,
	CheckContactRequest,
	CheckContactBatchRequest,
} from '@/types/customer'

// ============================================
// Query Keys
// ============================================

export const customerKeys = {
	all: ['customers'] as const,
	lists: () => [...customerKeys.all, 'list'] as const,
	list: (params?: { page?: number; page_size?: number } & CustomerFilter) =>
		[...customerKeys.lists(), params] as const,
	details: () => [...customerKeys.all, 'detail'] as const,
	detail: (id: number) => [...customerKeys.details(), id] as const,
}

// ============================================
// 查询 Hooks
// ============================================

interface UseCustomersParams extends CustomerFilter {
	page?: number
	page_size?: number
	enabled?: boolean
}

/**
 * 获取客户列表
 */
export function useCustomers(params?: UseCustomersParams) {
	return useQuery({
		queryKey: customerKeys.list(params),
		queryFn: () => customersApi.list(params),
		enabled: params?.enabled !== false,
	})
}

/**
 * 获取客户详情
 */
export function useCustomer(id: number, enabled = true) {
	return useQuery({
		queryKey: customerKeys.detail(id),
		queryFn: () => customersApi.get(id),
		enabled: enabled && id > 0,
	})
}

// ============================================
// 变更 Hooks
// ============================================

/**
 * 创建客户
 */
export function useCreateCustomer() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (data: CreateCustomerRequest) => customersApi.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
			toast.success('客户创建成功')
		},
	})
}

/**
 * 更新客户
 */
export function useUpdateCustomer() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ id, data }: { id: number; data: UpdateCustomerRequest }) =>
			customersApi.update(id, data),
		onSuccess: (_, { id }) => {
			queryClient.invalidateQueries({ queryKey: customerKeys.detail(id) })
			queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
			toast.success('客户更新成功')
		},
	})
}

/**
 * 删除客户
 */
export function useDeleteCustomer() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (id: number) => customersApi.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
			toast.success('客户删除成功')
		},
	})
}

/**
 * 转移客户
 */
export function useTransferCustomer() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: (data: TransferCustomerRequest) => customersApi.transfer(data),
		onSuccess: (_, { customer_id }) => {
			queryClient.invalidateQueries({ queryKey: customerKeys.detail(customer_id) })
			queryClient.invalidateQueries({ queryKey: customerKeys.lists() })
			toast.success('客户转移成功')
		},
	})
}

/**
 * 准备客户手动录单
 */
export function usePrepareManualOrder() {
	return useMutation({
		mutationFn: (id: number) => customersApi.prepareManualOrder(id),
	})
}

// ============================================
// 检查联系方式 Hooks
// ============================================

/**
 * 检查单个联系方式
 */
export function useCheckContact() {
	return useMutation({
		mutationFn: (data: CheckContactRequest) => customersApi.checkContact(data),
	})
}

/**
 * 批量检查联系方式
 */
export function useCheckContactBatch() {
	return useMutation({
		mutationFn: (data: CheckContactBatchRequest) => customersApi.checkContactBatch(data),
	})
}
